import { useState, useEffect, useCallback, useRef } from 'react';
import { Save, Plus, Trash2, Clock, Loader2, Calendar } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAutoSave } from '../../hooks/useAutoSave';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const ScheduleEditor = () => {
  const [schedules, setSchedules] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState('monday');
  const [localSlots, setLocalSlots] = useState({}); // Local edits per day

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  // Reference to track current day for save function
  const selectedDayRef = useRef(selectedDay);
  const localSlotsRef = useRef(localSlots);

  // Keep refs updated
  useEffect(() => {
    selectedDayRef.current = selectedDay;
  }, [selectedDay]);

  useEffect(() => {
    localSlotsRef.current = localSlots;
  }, [localSlots]);


  // Helper to normalize slots for saving (use 'note' not 'notes')
  const normalizeSlots = useCallback((slots) => {
    return slots.map(({ notes, ...slot }) => ({
      ...slot,
      note: slot.note || notes || ''
    }));
  }, []);

  // Save function for auto-save hook
  const performSave = useCallback(async () => {
    const day = selectedDayRef.current;
    const slots = localSlotsRef.current[day];

    if (!slots) return;

    // Sort chronologically and normalize field names before saving
    const sortedSlots = normalizeSlots([...slots])
      .sort((a, b) => a.start_time.localeCompare(b.start_time));

    const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/schedule/${day}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ time_slots: sortedSlots })
    });

    if (!response.ok) {
      throw new Error('Failed to save schedule');
    }

    // Refresh schedules after successful save
    const schedulesResponse = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/schedule`);
    if (schedulesResponse.ok) {
      const data = await schedulesResponse.json();
      setSchedules(data);
    }

    // Clear local slots for this day since we've saved
    setLocalSlots(prev => {
      const updated = { ...prev };
      delete updated[day];
      return updated;
    });
  }, []);

  const { isDirty, isSaving, scheduleAutosave, saveNow } = useAutoSave(performSave, 5000);

  useEffect(() => {
    fetchSchedules();
    fetchPrograms();
  }, []);

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/schedule`);
      if (response.ok) {
        const data = await response.json();
        setSchedules(data);
      }
    } catch (error) {
      console.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/programs`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('Failed to load programs');
    }
  };

  const getCurrentDaySlots = useCallback(() => {
    // Check for local edits first, otherwise use saved schedules
    if (localSlots[selectedDay]) {
      return localSlots[selectedDay];
    }
    const saved = schedules.find(s => s.day === selectedDay);
    if (!saved || !saved.time_slots) return [];
    // Sort time slots chronologically and normalize note/notes field
    return [...saved.time_slots]
      .map(slot => ({
        ...slot,
        note: slot.note || slot.notes || '' // Handle both 'note' and legacy 'notes' field
      }))
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [localSlots, schedules, selectedDay]);

  const addTimeSlot = async () => {
    const currentSlots = getCurrentDaySlots();
    const newSlot = {
      start_time: '17:00',
      end_time: '18:00',
      program_id: '',
      program_name: '',
      note: ''
    };

    const updatedSlots = [...currentSlots, newSlot];
    // Sort chronologically after adding and normalize
    const normalizedSlots = normalizeSlots(updatedSlots)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));

    // Save immediately for add operations
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/schedule/${selectedDay}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ time_slots: normalizedSlots })
      });
      if (response.ok) {
        fetchSchedules();
      }
    } catch (error) {
      console.error('Failed to add time slot');
    }
  };

  const updateTimeSlot = (index, field, value) => {
    const currentSlots = getCurrentDaySlots();
    const updatedSlots = [...currentSlots];

    if (field === 'program_id') {
      const selectedProgram = programs.find(p => p.id === value);
      updatedSlots[index] = {
        ...updatedSlots[index],
        program_id: value,
        program_name: selectedProgram ? selectedProgram.title : ''
      };
    } else {
      updatedSlots[index] = {
        ...updatedSlots[index],
        [field]: value
      };
    }

    // Update local state
    setLocalSlots(prev => ({ ...prev, [selectedDay]: updatedSlots }));

    // Schedule auto-save (5 second debounce)
    scheduleAutosave();
  };

  const deleteTimeSlot = async (index) => {
    const currentSlots = getCurrentDaySlots();
    const updatedSlots = currentSlots.filter((_, i) => i !== index);
    const normalizedSlots = normalizeSlots(updatedSlots);

    // Delete immediately saves
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/schedule/${selectedDay}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ time_slots: normalizedSlots })
      });
      if (response.ok) {
        // Clear any local edits for this day
        setLocalSlots(prev => {
          const updated = { ...prev };
          delete updated[selectedDay];
          return updated;
        });
        fetchSchedules();
      }
    } catch (error) {
      console.error('Failed to delete time slot');
    }
  };

  const clearDay = async () => {
    if (!window.confirm(`Clear all time slots for ${selectedDay}?`)) return;

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/schedule/${selectedDay}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        // Clear local edits for this day
        setLocalSlots(prev => {
          const updated = { ...prev };
          delete updated[selectedDay];
          return updated;
        });
        fetchSchedules();
      }
    } catch (error) {
      console.error('Failed to clear schedule');
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hour, minute] = time.split(':');
    const hour12 = hour > 12 ? hour - 12 : (hour === '00' ? 12 : hour);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minute} ${ampm}`;
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  const currentSlots = getCurrentDaySlots();

  return (
    <div className="space-y-6">
      <PageTitle
        title="Weekly Schedule"
        description="Manage class schedule for each day of the week"
        icon={<Calendar className="h-6 w-6" />}
        actions={
          <Button
            onClick={saveNow}
            disabled={!isDirty || isSaving}
            className="admin-btn-primary"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {isSaving ? 'Saving...' : 'Save Now'}
          </Button>
        }
      />

      {/* Day Selector */}
      <Card className="admin-card admin-corner-accent border-stone-200">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label className="text-gray-800 dark:text-gray-200 font-medium mb-3 block">Select Day</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                {daysOfWeek.map(day => {
                  const isSelected = day.key === selectedDay;
                  return (
                    <button
                      key={day.key}
                      onClick={() => setSelectedDay(day.key)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all admin-touch:min-h-[44px] ${
                        isSelected
                          ? 'bg-amber-500 text-white shadow-md'
                          : 'bg-stone-100 text-gray-700 hover:bg-amber-100 hover:text-amber-900 border border-stone-200'
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={addTimeSlot}
                variant="adminPrimary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Time Slot
              </Button>
              <Button
                onClick={clearDay}
                variant="adminOutline"
                className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Day
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Time Slots for Selected Day */}
      <Card className="admin-card admin-corner-accent border-stone-200">
        <CardHeader className="border-b border-stone-200 bg-gradient-to-r from-stone-50 to-amber-50/30 dark:from-stone-900 dark:to-amber-950/30">
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-gray-900 dark:text-gray-100">
            <span className="flex items-center gap-2">
              <span className="text-amber-600 dark:text-amber-500">●</span>
              {daysOfWeek.find(d => d.key === selectedDay)?.label} Schedule
              {currentSlots.length > 0 && (
                <span className="text-sm font-normal text-stone-600 dark:text-stone-400 ml-2">
                  ({currentSlots.length} time slot{currentSlots.length !== 1 ? 's' : ''})
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              {isSaving && (
                <span className="text-sm font-normal text-amber-700 dark:text-amber-500 flex items-center">
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </span>
              )}
              {isDirty && !isSaving && (
                <Button
                  size="sm"
                  variant="adminGold"
                  onClick={saveNow}
                >
                  <Save className="h-4 w-4 mr-1" />
                  Save Now
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {currentSlots.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <Clock className="h-12 w-12 mx-auto mb-3 text-stone-400" />
              <p>No time slots scheduled for this day</p>
              <p className="text-sm">Click "Add Time Slot" to create one</p>
            </div>
          ) : (
            <div className="space-y-4">
              {currentSlots.map((slot, index) => (
                <Card key={index} className="border-l-4 border-l-amber-500 bg-background/80 border-stone-200">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor={`start-${index}`} className="text-gray-800 dark:text-gray-200 font-medium">Start Time</Label>
                        <Input
                          id={`start-${index}`}
                          type="time"
                          value={slot.start_time}
                          onChange={(e) => updateTimeSlot(index, 'start_time', e.target.value)}
                          className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                        />
                      </div>

                      <div>
                        <Label htmlFor={`end-${index}`} className="text-gray-800 dark:text-gray-200 font-medium">End Time</Label>
                        <Input
                          id={`end-${index}`}
                          type="time"
                          value={slot.end_time}
                          onChange={(e) => updateTimeSlot(index, 'end_time', e.target.value)}
                          className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                        />
                      </div>

                      <div>
                        <Label htmlFor={`program-${index}`} className="text-gray-800 dark:text-gray-200 font-medium">Program (Optional)</Label>
                        <Select
                          value={slot.program_id || 'none'}
                          onValueChange={(value) => updateTimeSlot(index, 'program_id', value === 'none' ? '' : value)}
                        >
                          <SelectTrigger id={`program-${index}`} className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background">
                            <SelectValue placeholder="No program selected" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Program</SelectItem>
                            {programs.map(program => (
                              <SelectItem key={program.id} value={program.id}>
                                {program.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-end">
                        <Button
                          size="sm"
                          variant="adminOutline"
                          onClick={() => deleteTimeSlot(index)}
                          className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3">
                      <Label htmlFor={`note-${index}`} className="text-gray-800 dark:text-gray-200 font-medium">Note (Optional)</Label>
                      <Input
                        id={`note-${index}`}
                        value={slot.note || ''}
                        onChange={(e) => updateTimeSlot(index, 'note', e.target.value)}
                        placeholder="e.g., Advanced students only"
                        className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                      />
                    </div>

                    <div className="mt-3 p-2 bg-amber-50/50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 text-sm text-stone-700 dark:text-stone-300">
                      <strong className="text-gray-900 dark:text-gray-100">Preview:</strong> {formatTime(slot.start_time)} - {formatTime(slot.end_time)} • {slot.program_name || 'No program selected'}
                      {slot.note && <span> • {slot.note}</span>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default ScheduleEditor;
